import { LookupTableItem, NestedLookupTableItem } from "@/types";

export interface Search {
  currentSearchQuery: SearchQuery;
  lastSearchQuery: SearchQuery | null;
  lastSearchQueryPagination: SearchResultsPagination;
  cachedResults: SearchItem[][];
  keepCachedResults: boolean;
  autocomplete: {
    isLoading: boolean;
    results: SearchItem[];
  };
  isLoading: boolean;
}

export interface SearchItem {
  subscriptionModules: number[];
  id: number;
  briefcaseId: number;
  positionSummary: string;
  preferredNameDisplay: string;
  title: string;
  politicalParty: string;
  quickSummary: string;
  headShot: string;
  addedToBriefcase: boolean;
  isFormer: boolean;
}

export interface SearchResultsPagination {
  pageSize: number;
  page: number;
  totalPages: number;
  totalHits: number;
  order: SearchOrder;
}

export enum SearchOrder {
  DEFAULT = "",
  RELEVANCE = "relevance",
  ALPHABETICAL = "alphabetical",
}

export enum ProfileView {
  DEFAULT = "",
  CARD = "card",
  LIST = "list",
}

export interface SearchQueryUpdatePayload {
  field: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

// Local search variables
export interface SearchQuery {
  // Search configuration
  page: number;
  pageSize: number;
  order: SearchOrder;
  view: ProfileView; // "" = default (card) / "card" / "list"
  // override flag to hide pay-walled profiles, otherwise fallback to user preference
  hidePayWalledProfiles: boolean | undefined;

  // Local variables
  autoCompleteKeyword: string;
  advancedSearchOpen: boolean;

  // Vignette simple search
  keyword: string;

  // Vignette advanced search
  currentOfficeCongressionalDistrict: LookupTableItem;
  currentOfficeStates: LookupTableItem[];
  politicalParty: LookupTableItem;
  religion: LookupTableItem;
  ethnicity: LookupTableItem;
  gender: LookupTableItem;
  state: LookupTableItem;
  country: LookupTableItem;
  editorialGroup: LookupTableItem;
  profileTypes: NestedLookupTableItem[];
  keyPolicyAreas: NestedLookupTableItem[];
  industries: NestedLookupTableItem[];
  committees: NestedLookupTableItem[];
  caucuses: NestedLookupTableItem[];
  agencies: NestedLookupTableItem[];
  city: string;
  education: string;
  includeFormer: boolean;
  // Birthdate
  birthYear: LookupTableItem;
  age: LookupTableItem;
  birthDateOperator: LookupTableItem;
  // Special Interest Groups - NestedLookupTableItem but nullable
  sig: NestedLookupTableItem | null;
  sigOperator: LookupTableItem;
  sigRating: LookupTableItem;
}

// This is the parsed and stringified version of SearchQuery object, closer to what API is expecting for each filter.
// SearchQuery object is used for UI components.
// SearchQueryStrings is used for making local comparisons and API requests easier.
export interface SearchQueryStrings {
  // keyword search query and search configurations
  q: string;
  page: number; // >= 0
  pageSize: number; // backend default = 20, we use 12
  order: SearchOrder; // "" = default (relevance) / "relevance" / "alphabetical"
  view: ProfileView; // "" = default (card) / "card" / "list"

  // user preferences
  hidePayWalledProfiles: boolean | undefined;

  // advanced search queries
  // TODO: it will require input of the form "NY-12" for a congressional district.
  current_office_congressional_district: string; // LookupTables.states, single state ID
  gender: string; // LookupTables.genders
  political_party: string; // LookupTables.politicalParties
  religion: string; // LookupTables.religions
  ethnicity: string; // LookupTables.ethnicities
  current_office_state_abbr: string; // LookupTables.states, comma separated IDs
  city: string; // text
  state: string; // LookupTables.states
  country: string; // LookupTables.countries
  education: string; // text
  editorial_group: string; // LookupTables.editorialGroups, single ID
  profile_types: string; // LookupTables.profileTypes, comma separated IDs
  key_policy_areas: string; // keyPolicyAreasLookupTable, comma separated IDs
  industry: string; // industryLookupTable, comma separated IDs
  include_former: boolean; // Whether to include archived profiles
  committees: string; // committeesLookupTable, comma separated IDs
  caucuses: string; // caucusesLookupTable, comma separated IDs
  agency: string; // caucusesLookupTable, comma separated IDs
  // Special Interest Groups
  sig_id: string; // sigsLookupTable, (ID)
  sig_operator: string; // LookupTables.sigOperator
  sig_rating: string; // LookupTables.sigRatingOptions
  // UI selections - mapped to birth_year
  birth_year_selection: string; // LookupTables.birthYearOptions, calculated in ApiService, YYYY (eq), <YYYY (lt), >YYYY (gte)
  age_selection: string; // LookupTables.ageOptions
  birth_date_operator: string; // LookupTables.birthDateOperator
}
